import React, { useState, useEffect } from 'react';
import withRoleAccess from '../../hoc/withRoleAccess';
import {
  Card,
  Row,
  Col,
  DatePicker,
  Button,
  Statistic,
  Spin,
  Alert,
  Typography,
  Space,
  Avatar,
  Tag,
  Progress,
  Divider,
  message
} from 'antd';
import {
  UserOutlined,
  TrophyOutlined,
  ReloadOutlined,
  BookOutlined,
  ClockCircleOutlined,
  CalendarOutlined,
  FilePdfOutlined,
  TeamOutlined,
  StarOutlined,
  ReadOutlined,
  CheckCircleOutlined,
  ClockCircleFilled,
  FileTextOutlined
} from '@ant-design/icons';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import dayjs from 'dayjs';

const { RangePicker } = DatePicker;
const { Title, Text } = Typography;

const API_URL = process.env.REACT_APP_API_URL;

const TeacherReport = () => {
const [dateRange, setDateRange] = useState([
  dayjs().startOf('year'), 
  dayjs().endOf('year')    
]);

  
  const [hoursData, setHoursData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Real API call using fetch
  const fetchHoursData = async () => {
    const token = localStorage.getItem('token');
    const response = await fetch(`${API_URL}/report/teacher-reports/hours?from=${dateRange[0].format('YYYY-MM-DD')}&to=${dateRange[1].format('YYYY-MM-DD')}`, {
      headers: { Authorization: `Bearer ${token}` }
    });
    if (!response.ok) throw new Error('Failed to fetch hours data');
    return await response.json();
  };

  const loadData = async () => {
    if (!API_URL) {
      setError('API URL is not configured. Please set REACT_APP_API_URL in your environment variables.');
      return;
    }

    const token = localStorage.getItem('token');
    if (!token) {
      setError('Authentication token not found. Please log in again.');
      return;
    }

    setLoading(true);
    setError(null);
    
    try {
      const hours = await fetchHoursData();
      setHoursData(hours);
      message.success('Teacher report data loaded successfully');
    } catch (err) {
      console.error('Error loading teacher report data:', err);
      
      let errorMessage = 'Failed to load teacher report data';
      if (err.status) {
        if (err.status === 401) {
          errorMessage = 'Authentication failed. Please log in again.';
        } else if (err.status === 400) {
          errorMessage = 'Invalid request parameters';
        } else {
          errorMessage = `Server error: ${err.status}`;
        }
      } else {
        errorMessage = err.message || 'An unexpected error occurred';
      }
      
      setError(errorMessage);
      message.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (dateRange && dateRange.length === 2 && dateRange[0] && dateRange[1]) {
      loadData();
    }
  }, [dateRange]);

  const handleDateRangeChange = (dates) => {
    if (dates && dates.length === 2) {
      setDateRange(dates);
    }
  };

  const handleManualRefresh = () => {
    loadData();
  };

  const handlePrintPDF = () => {
    // Open a new window for printing
    const printWindow = window.open('', '_blank', 'width=800,height=600');
    
    // Get the content to print
    const reportContainer = document.getElementById('teacher-report-container');
    const printContent = reportContainer ? reportContainer.innerHTML : '';
    
    // Create the print document
    const printDocument = `
      <!DOCTYPE html>
      <html>
        <head>
          <title>Teacher Performance Report</title>
          <link rel="stylesheet" href="https://cdnjs.cloudflare.com/ajax/libs/antd/5.0.0/reset.css">
          <style>
            body { 
              font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
              margin: 0;
              padding: 20px;
              background: white;
            }
            .ant-card { 
              box-shadow: none !important; 
              border: 1px solid #d9d9d9 !important;
              margin-bottom: 16px;
              page-break-inside: avoid;
            }
            .ant-statistic-title { font-weight: bold !important; }
            .ant-statistic-content { font-size: 18px !important; }
            .ant-card-head { 
              border-bottom: 2px solid #1890ff !important; 
              background: #f0f2f5 !important;
            }
            .ant-card-head-title { 
              font-weight: bold !important; 
              color: #1890ff !important; 
            }
            .print-header { 
              display: block !important;
              text-align: center; 
              margin-bottom: 20px; 
              border-bottom: 3px solid #1890ff; 
              padding-bottom: 10px; 
            }
            .print-date-range { 
              display: block !important;
              text-align: center; 
              margin-bottom: 15px; 
              font-weight: bold; 
            }
            svg { max-width: 100% !important; height: auto !important; }
            .no-print { display: none !important; }
            @page { 
              margin: 15mm; 
              size: A4; 
            }
          </style>
        </head>
        <body>
          ${printContent}
          <script>
            window.onload = function() {
              window.print();
              window.onafterprint = function() {
                window.close();
              };
            };
          </script>
        </body>
      </html>
    `;
    
    // Write the document and trigger print
    printWindow.document.write(printDocument);
    printWindow.document.close();
  };

  // Prepare chart data for hours breakdown
  const topTeachersChartData = hoursData?.top_teachers?.slice(0, 10).map(teacher => {
    const name = teacher.nickname || teacher.teacher_name || 'Unknown';
    return {
      name: name.length > 15 ? name.substring(0, 15) + '...' : name,
      approved_hours: parseFloat(teacher.approved_hours) || 0,
      pending_hours: parseFloat(teacher.pending_hours) || 0,
      total_hours: parseFloat(teacher.total_hours) || 0,
      approved: parseInt(teacher.approved_count) || 0,
      pending: parseInt(teacher.pending_count) || 0,
      total: parseInt(teacher.total_classes) || 0
    };
  }) || [];

  // Prepare chart data for class counts
  const classCountChartData = hoursData?.top_teachers?.slice(0, 10).map(teacher => {
    const name = teacher.nickname || teacher.teacher_name || 'Unknown';
    return {
      name: name.length > 15 ? name.substring(0, 15) + '...' : name,
      approved: parseInt(teacher.approved_count) || 0,
      pending: parseInt(teacher.pending_count) || 0,
      total: parseInt(teacher.total_classes) || 0
    };
  }) || [];

  // Prepare teacher-subject mapping data for horizontal bar chart
  const teacherSubjectData = hoursData?.top_teachers?.slice(0, 12)
    .filter(teacher => teacher.most_taught_subject) // Only include teachers with subjects
    .map(teacher => {
      const name = teacher.nickname || teacher.teacher_name || 'Unknown';
      return {
        teacher: name.length > 20 ? name.substring(0, 20) + '...' : name,
        subject: teacher.most_taught_subject,
        hours: parseFloat(teacher.approved_hours) || 0,
        classes: parseInt(teacher.approved_count) || 0
      };
    }) || [];

  const HourBreakdownTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      return (
        <div className="bg-white p-3 border border-gray-200 rounded-lg shadow-lg">
          <p className="font-semibold">{`${label}`}</p>
          {payload.map((entry, index) => (
            <p key={index} style={{ color: entry.color }}>
              {`${entry.dataKey}: ${entry.value.toFixed(2)}h`}
            </p>
          ))}
        </div>
      );
    }
    return null;
  };

  const TeacherSubjectTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      const data = payload[0].payload;
      return (
        <div className="bg-white p-3 border border-gray-200 rounded-lg shadow-lg">
          <p className="font-semibold">{`${data.teacher}`}</p>
          <p className="text-blue-600">{`Subject: ${data.subject}`}</p>
          <p className="text-green-600">{`Hours: ${data.hours}h`}</p>
          <p className="text-purple-600">{`Classes: ${data.classes}`}</p>
        </div>
      );
    }
    return null;
  };

  const ClassCountTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      return (
        <div className="bg-white p-3 border border-gray-200 rounded-lg shadow-lg">
          <p className="font-semibold">{`${label}`}</p>
          {payload.map((entry, index) => (
            <p key={index} style={{ color: entry.color }}>
              {`${entry.dataKey}: ${entry.value}`}
            </p>
          ))}
        </div>
      );
    }
    return null;
  };

  const PieTooltip = ({ active, payload }) => {
    if (active && payload && payload.length) {
      return (
        <div className="bg-white p-3 border border-gray-200 rounded-lg shadow-lg">
          <p className="font-semibold">{`${payload[0].name}`}</p>
          <p className="text-blue-600">
            {`Teachers: ${payload[0].value}`}
          </p>
        </div>
      );
    }
    return null;
  };

  // Calculate summary stats
  const totalTeachers = hoursData?.top_teachers?.length || 0;
  const averageHours = parseFloat(hoursData?.average_hours) || 0;
  const totalHours = hoursData?.top_teachers?.reduce((sum, teacher) => sum + parseFloat(teacher.total_hours || 0), 0) || 0;
  const totalApprovedHours = hoursData?.top_teachers?.reduce((sum, teacher) => sum + parseFloat(teacher.approved_hours || 0), 0) || 0;
  const totalPendingHours = hoursData?.top_teachers?.reduce((sum, teacher) => sum + parseFloat(teacher.pending_hours || 0), 0) || 0;
  const totalApprovedClasses = hoursData?.top_teachers?.reduce((sum, teacher) => sum + parseInt(teacher.approved_count || 0), 0) || 0;
  const totalPendingClasses = hoursData?.top_teachers?.reduce((sum, teacher) => sum + parseInt(teacher.pending_count || 0), 0) || 0;
  const totalClasses = hoursData?.top_teachers?.reduce((sum, teacher) => sum + parseInt(teacher.total_classes || 0), 0) || 0;
  const mostActiveTeacher = hoursData?.top_teachers?.[0];
  const uniqueSubjects = [...new Set(hoursData?.top_teachers?.map(t => t.most_taught_subject).filter(Boolean))] || [];
  const totalSubjects = uniqueSubjects.length;

  return (
    <div style={{ padding: '24px', backgroundColor: '#f5f5f5', minHeight: '100vh' }}>
      <div id="teacher-report-container" style={{ maxWidth: '1400px', margin: '0 auto' }}>
        {/* Print Header - Only visible when printing */}
        <div className="print-header" style={{ display: 'none' }}>
          <Title level={2} style={{ margin: 0, color: '#1890ff' }}>
            Teacher Performance Report
          </Title>
          <Text>Generated on {new Date().toLocaleDateString()}</Text>
        </div>

        {/* Print Date Range - Only visible when printing */}
        <div className="print-date-range" style={{ display: 'none' }}>
          Report Period: {dateRange[0]?.format('YYYY-MM-DD')} to {dateRange[1]?.format('YYYY-MM-DD')}
        </div>

        {/* Header */}
        <div style={{ marginBottom: '24px' }}>
          <Title level={2} style={{ marginBottom: '8px' }}>
            Teacher Performance Report
          </Title>
          <Text type="secondary">
            Comprehensive overview of teacher hours, class counts, and subject specializations
          </Text>
        </div>

        {/* Date Range and Controls */}
        <Card style={{ marginBottom: '24px' }}>
          <Space size="large" wrap>
            <Space>
              <CalendarOutlined />
              <Text strong>Date Range:</Text>
              <RangePicker
                value={dateRange}
                onChange={handleDateRangeChange}
                format="YYYY-MM-DD"
                allowClear={false}
              />
            </Space>
            <Space>
              <Button 
                type="primary" 
                icon={<ReloadOutlined />}
                onClick={handleManualRefresh}
                loading={loading}
              >
                Update Report
              </Button>
              <Button 
                type="default" 
                icon={<FilePdfOutlined />}
                onClick={handlePrintPDF}
                disabled={!hoursData}
              >
                Print to PDF
              </Button>
            </Space>
          </Space>
        </Card>

        {error && (
          <Alert
            message="Error"
            description={error}
            type="error"
            showIcon
            style={{ marginBottom: '24px' }}
          />
        )}

        <Spin spinning={loading}>
          {/* Key Metrics */}
          <Row gutter={[16, 16]} style={{ marginBottom: '24px' }}>
            <Col xs={24} sm={12} lg={6}>
              <Card>
                <Statistic
                  title="Total Teachers"
                  value={totalTeachers}
                  prefix={<TeamOutlined />}
                  valueStyle={{ color: '#1890ff' }}
                />
              </Card>
            </Col>
            <Col xs={24} sm={12} lg={6}>
              <Card>
                <Statistic
                  title="Total Classes"
                  value={totalClasses}
                  prefix={<FileTextOutlined />}
                  valueStyle={{ color: '#722ed1' }}
                />
                <div style={{ marginTop: '8px', fontSize: '12px' }}>
                  <Text type="success">Approved: {totalApprovedClasses}</Text>
                  <br />
                  <Text type="warning">Pending: {totalPendingClasses}</Text>
                </div>
              </Card>
            </Col>
            <Col xs={24} sm={12} lg={6}>
              <Card>
                <Statistic
                  title="Total Hours"
                  value={totalApprovedHours}
                  precision={2}
                  prefix={<ClockCircleOutlined />}
                  valueStyle={{ color: '#52c41a' }}
                />
                <div style={{ marginTop: '8px', fontSize: '12px' }}>
                  <Text type="secondary">Approved: {totalApprovedHours.toFixed(1)}h</Text>
                  <br />
                  <Text type="warning">Pending: {totalPendingHours.toFixed(1)}h</Text>
                </div>
              </Card>
            </Col>
            <Col xs={24} sm={12} lg={6}>
              <Card>
                <Statistic
                  title="Average Hours"
                  value={averageHours}
                  precision={2}
                  prefix={<ClockCircleOutlined />}
                  valueStyle={{ color: '#f5222d' }}
                />
              </Card>
            </Col>
          </Row>

          {/* Charts Row */}
          <Row gutter={[16, 16]} style={{ marginBottom: '24px' }}>
            {/* Top Teachers by Hours Breakdown Chart */}
            <Col xs={24} lg={12}>
              <Card 
                title={
                  <Space>
                    <TrophyOutlined style={{ color: '#faad14' }} />
                    Top Teachers by Hours Breakdown
                  </Space>
                }
              >
                {topTeachersChartData.length > 0 ? (
                  <ResponsiveContainer width="100%" height={300}>
                    <BarChart data={topTeachersChartData} margin={{ top: 20, right: 30, left: 20, bottom: 5 }}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="name" angle={-45} textAnchor="end" height={100} />
                      <YAxis />
                      <Tooltip content={<HourBreakdownTooltip />} />
                      <Bar dataKey="approved_hours" stackId="a" fill="#52c41a" name="Approved Hours" />
                      <Bar dataKey="pending_hours" stackId="a" fill="#faad14" name="Pending Hours" />
                    </BarChart>
                  </ResponsiveContainer>
                ) : (
                  <div style={{ textAlign: 'center', padding: '60px 0' }}>
                    <Text type="secondary">No teaching hours data available</Text>
                  </div>
                )}
              </Card>
            </Col>

            {/* Class Status Distribution Chart */}
            <Col xs={24} lg={12}>
              <Card 
                title={
                  <Space>
                    <CheckCircleOutlined style={{ color: '#52c41a' }} />
                    Class Status Distribution
                  </Space>
                }
              >
                {classCountChartData.length > 0 ? (
                  <ResponsiveContainer width="100%" height={300}>
                    <BarChart data={classCountChartData} margin={{ top: 20, right: 30, left: 20, bottom: 5 }}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="name" angle={-45} textAnchor="end" height={100} />
                      <YAxis />
                      <Tooltip content={<ClassCountTooltip />} />
                      <Bar dataKey="approved" stackId="a" fill="#52c41a" name="Approved" />
                      <Bar dataKey="pending" stackId="a" fill="#faad14" name="Pending" />
                    </BarChart>
                  </ResponsiveContainer>
                ) : (
                  <div style={{ textAlign: 'center', padding: '60px 0' }}>
                    <Text type="secondary">No class data available</Text>
                  </div>
                )}
              </Card>
            </Col>
          </Row>

          {/* Teacher Subject Specialization Chart */}
          <Row gutter={[16, 16]} style={{ marginBottom: '24px' }}>
            <Col xs={24} lg={16}>
  <Card 
    title={
      <Space>
        <ReadOutlined style={{ color: '#1890ff' }} />
        Teacher Subject Specializations
      </Space>
    }
  >
    {teacherSubjectData.length > 0 ? (
      <div style={{ maxHeight: '350px', overflowY: 'auto' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead>
            <tr style={{ backgroundColor: '#f5f5f5', borderBottom: '2px solid #d9d9d9' }}>
              <th style={{ padding: '12px', textAlign: 'left', fontWeight: 'bold' }}>Rank</th>
              <th style={{ padding: '12px', textAlign: 'left', fontWeight: 'bold' }}>Teacher</th>
              <th style={{ padding: '12px', textAlign: 'left', fontWeight: 'bold' }}>Subject</th>
              <th style={{ padding: '12px', textAlign: 'right', fontWeight: 'bold' }}>Approved Hours</th>
              <th style={{ padding: '12px', textAlign: 'right', fontWeight: 'bold' }}>Total Approved Classes</th>
            </tr>
          </thead>
          <tbody>
            {teacherSubjectData.map((teacher, index) => (
              <tr 
                key={index}
                style={{ 
                  borderBottom: '1px solid #f0f0f0',
                  backgroundColor: index % 2 === 0 ? '#fafafa' : 'white'
                }}
              >
                <td style={{ padding: '10px', fontWeight: 'bold', color: '#1890ff' }}>
                  #{index + 1}
                </td>
                <td style={{ padding: '10px' }}>
                  <Text strong>{teacher.teacher}</Text>
                </td>
                <td style={{ padding: '10px' }}>
                  <Tag color="blue">{teacher.subject}</Tag>
                </td>
                <td style={{ padding: '10px', textAlign: 'right' }}>
                  <Text style={{ color: '#52c41a', fontWeight: 'bold' }}>
                    {teacher.hours.toFixed(1)}h
                  </Text>
                </td>
                <td style={{ padding: '10px', textAlign: 'right' }}>
                  <Text style={{ color: '#722ed1' }}>
                    {teacher.classes}
                  </Text>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    ) : (
      <div style={{ textAlign: 'center', padding: '60px 0' }}>
        <Text type="secondary">No teacher subject data available</Text>
      </div>
    )}
  </Card>
</Col>
            {/* Class Approval Rate */}
            <Col xs={24} lg={8}>
              <Card title="Class Approval Overview">
                <Space direction="vertical" size="large" style={{ width: '100%' }}>
                  <Row gutter={16}>
                    <Col span={8}>
                      <Statistic
                        title="Approved"
                        value={totalApprovedClasses}
                        valueStyle={{ color: '#52c41a' }}
                        prefix={<CheckCircleOutlined />}
                      />
                    </Col>
                    <Col span={8}>
                      <Statistic
                        title="Pending"
                        value={totalPendingClasses}
                        valueStyle={{ color: '#faad14' }}
                        prefix={<ClockCircleFilled />}
                      />
                    </Col>
                    <Col span={8}>
                      <Statistic
                        title="Total"
                        value={totalClasses}
                        valueStyle={{ color: '#1890ff' }}
                        prefix={<FileTextOutlined />}
                      />
                    </Col>
                  </Row>
                  
                  <Divider />
                  
                  <div>
                    <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px' }}>
                      <Text>Approval Rate</Text>
                      <Text strong style={{ color: '#52c41a' }}>
                        {totalClasses > 0 ? ((totalApprovedClasses / totalClasses) * 100).toFixed(1) : 0}%
                      </Text>
                    </div>
                    <Progress 
                      percent={totalClasses > 0 ? ((totalApprovedClasses / totalClasses) * 100) : 0}
                      strokeColor="#52c41a"
                      showInfo={false}
                    />
                    <Text type="secondary" style={{ fontSize: '12px' }}>
                      {totalApprovedClasses} of {totalClasses} classes approved
                    </Text>
                  </div>
                  
                  <div>
                    <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px' }}>
                      <Text>Hours Breakdown</Text>
                    </div>
                    <div style={{ marginBottom: '8px' }}>
                      <Text type="secondary">Approved: </Text>
                      <Text strong style={{ color: '#52c41a' }}>{totalApprovedHours.toFixed(1)}h</Text>
                    </div>
                    <div>
                      <Text type="secondary">Pending: </Text>
                      <Text strong style={{ color: '#faad14' }}>{totalPendingHours.toFixed(1)}h</Text>
                    </div>
                  </div>
                </Space>
              </Card>
            </Col>
          </Row>

          {/* Performance Insights and Teacher Leaderboard */}
          <Row gutter={[16, 16]} style={{ marginBottom: '24px' }}>
            <Col xs={24} lg={12}>
              <Card title="Performance Insights">
                <Space direction="vertical" size="large" style={{ width: '100%' }}>
                  {mostActiveTeacher && (
                    <>
                      <div>
                        <Text strong style={{ color: '#1890ff' }}>Most Active Teacher</Text>
                        <br />
                        <Text>{mostActiveTeacher.nickname || mostActiveTeacher.teacher_name}</Text>
                        <br />
                        <Text type="secondary">
                          {parseFloat(mostActiveTeacher.total_hours).toFixed(2)} hours taught total
                        </Text>
                        <br />
                        <Text type="secondary">
                          ({parseFloat(mostActiveTeacher.approved_hours).toFixed(2)}h approved, {parseFloat(mostActiveTeacher.pending_hours).toFixed(2)}h pending)
                        </Text>
                        <br />
                        <Text type="secondary">
                          {mostActiveTeacher.total_classes} classes total 
                          ({mostActiveTeacher.approved_count} approved, {mostActiveTeacher.pending_count} pending)
                        </Text>
                        {mostActiveTeacher.most_taught_subject && (
                          <>
                            <br />
                            <Text type="secondary">
                              Most taught subject: <Text strong style={{ color: '#1890ff' }}>{mostActiveTeacher.most_taught_subject}</Text>
                            </Text>
                          </>
                        )}
                      </div>
                      
                      <Divider />
                      
                      <div>
                        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px' }}>
                          <Text>Teacher Productivity</Text>
          <Text strong style={{ color: '#52c41a' }}>
                            {averageHours > 0 ? ((parseFloat(mostActiveTeacher.total_hours) / averageHours) * 100).toFixed(0) : 0}%
                          </Text>
                        </div>
                        <Progress 
                          percent={averageHours > 0 ? Math.min(((parseFloat(mostActiveTeacher.total_hours) / averageHours) * 100), 100) : 0}
                          strokeColor="#52c41a"
                          showInfo={false}
                        />
                        <Text type="secondary" style={{ fontSize: '12px' }}>
                          Compared to average teacher hours
                        </Text>
                      </div>
                    </>
                  )}
                  
                  <Divider />
                  
                  <Row gutter={16}>
                    <Col span={12}>
                      <Statistic
                        title="Total Minutes"
                        value={totalApprovedHours * 60}
                        precision={0}
                        valueStyle={{ fontSize: '18px', color: '#722ed1' }}
                      />
                    </Col>
                    <Col span={12}>
                      <div>
                        <Text type="secondary">Popular Subject</Text>
                        <br />
                        <Text strong style={{ fontSize: '18px' }}>
                          {uniqueSubjects.length > 0 ? uniqueSubjects[0] : 'N/A'}
                        </Text>
                      </div>
                    </Col>
                  </Row>
                </Space>
              </Card>
            </Col>

            {/* Teacher Leaderboard */}
            <Col xs={24} lg={12}>
              <Card title="Teaching Hours Leaderboard">
                {hoursData?.top_teachers?.length > 0 ? (
                  <div style={{ maxHeight: '400px', overflowY: 'auto' }}>
                    <Space direction="vertical" size="middle" style={{ width: '100%' }}>
                      {hoursData.top_teachers.slice(0, 8).map((teacher, index) => (
                        <Card key={teacher.teacher_id} size="small">
                          <Space align="start" style={{ width: '100%' }}>
                            <Avatar
                              icon={<UserOutlined />}
                              style={{
                                backgroundColor: index === 0 ? '#faad14' : index === 1 ? '#d9d9d9' : index === 2 ? '#cd7f32' : '#1890ff',
                                border: index < 3 ? '2px solid' : 'none',
                                borderColor: index === 0 ? '#faad14' : index === 1 ? '#d9d9d9' : '#cd7f32'
                              }}
                            />
                            <div style={{ flex: 1 }}>
                              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                <Text strong>
                                  {teacher.nickname || teacher.teacher_name}
                                  {index === 0 && <StarOutlined style={{ color: '#faad14', marginLeft: 8 }} />}
                                </Text>
                                <Text style={{ color: '#52c41a', fontWeight: 'bold' }}>
                                  {parseFloat(teacher.total_hours).toFixed(2)}h
                                </Text>
                              </div>
                              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: '4px' }}>
                                <Text type="secondary">Rank #{index + 1}</Text>
                                <Space size="small">
                                  <Tag color="green">{teacher.approved_count} approved</Tag>
                                  <Tag color="orange">{teacher.pending_count} pending</Tag>
                                </Space>
                              </div>
                              <div style={{ marginTop: '4px' }}>
                                {teacher.most_taught_subject && (
                                  <Tag color="blue" style={{ margin: 0 }}>
                                    Subject: {teacher.most_taught_subject}
                                  </Tag>
                                )}
                                {!teacher.most_taught_subject && (
                                  <Tag color="default" style={{ margin: 0 }}>
                                    No approved classes
                                  </Tag>
                                )}
                              </div>
                            </div>
                          </Space>
                        </Card>
                      ))}
                    </Space>
                  </div>
                ) : (
                  <div style={{ textAlign: 'center', padding: '40px 0' }}>
                    <Text type="secondary">No teaching hours data available</Text>
                  </div>
                )}
              </Card>
            </Col>
          </Row>
        </Spin>

        <style jsx>{`
          .print-header, .print-date-range {
            display: none;
          }
          
          @media print {
            .print-header, .print-date-range {
              display: block !important;
            }
          }
        `}</style>
      </div>
    </div>
  );
};

export default withRoleAccess(TeacherReport, ['superadmin']);